import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const PRIORITY_VARIANT_ID = 67717852332267;
const CHECKOUT_ONLY_DISCOUNT_CODES = ['TRANSFERENCIA', 'EFECTIVO'];

let priorityMutationPromise = null;

async function getCart() {
  const response = await fetch(Theme.routes.cart_url + '.js', {
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
    },
    cache: 'no-store',
  });

  if (!response.ok) throw new Error('No se pudo leer el carrito');
  return response.json();
}

function findPriorityItem(cart) {
  return (cart.items || []).find(
    (item) => Number(item.variant_id) === PRIORITY_VARIANT_ID
  );
}

function visibleItemCount(cart) {
  return (cart.items || []).reduce((total, item) => {
    return Number(item.variant_id) === PRIORITY_VARIANT_ID
      ? total
      : total + Number(item.quantity || 0);
  }, 0);
}

async function updateHeaderCartCount(cart) {
  const icon = document.querySelector('cart-icon');
  if (icon && typeof icon.renderCartBubble === 'function') {
    await icon.renderCartBubble(visibleItemCount(cart), false);
  }
}

async function refreshCartSection(element, sectionHtml = null) {
  const sectionId = element.dataset.sectionId;
  if (!sectionId) return;

  try {
    let html = sectionHtml;

    // Prefer the section HTML returned by the same cart mutation. This avoids
    // a second request racing against the drawer on desktop.
    if (!html) {
      const response = await fetch(
        window.location.pathname + '?section_id=' + encodeURIComponent(sectionId),
        {
          headers: { Accept: 'text/html' },
          cache: 'no-store',
        }
      );

      if (!response.ok) throw new Error('No se pudo refrescar el carrito');
      html = await response.text();
    }

    await morphSection(sectionId, html, {
      mode: element.closest('theme-drawer') ? 'hydration' : 'full',
    });
  } catch (error) {
    console.warn('[REFYN] Cart section refresh failed', error);
  }
}

async function clearCheckoutOnlyDiscounts(cart) {
  const currentCodes = (cart.discount_codes || [])
    .map((discount) => discount?.code)
    .filter(Boolean);

  const keptCodes = currentCodes.filter(
    (code) => !CHECKOUT_ONLY_DISCOUNT_CODES.includes(String(code).toUpperCase())
  );

  if (keptCodes.length === currentCodes.length) return cart;

  const response = await fetch(
    Theme.routes.cart_update_url,
    fetchConfig('json', {
      body: JSON.stringify({
        discount: keptCodes.join(','),
      }),
    })
  );

  if (!response.ok) throw new Error('No se pudieron limpiar los descuentos del checkout');
  return response.json();
}

async function setPriorityState(shouldBeActive, cartBefore, sectionId) {
  const currentItem = findPriorityItem(cartBefore);

  // Make the operation idempotent: if the cart is already in the requested
  // state, just return the current cart instead of adding/removing again.
  if (Boolean(currentItem) === shouldBeActive) {
    return { cart: cartBefore, sectionHtml: null };
  }

  const url = shouldBeActive
    ? Theme.routes.cart_add_url
    : Theme.routes.cart_change_url;

  const body = shouldBeActive
    ? {
        items: [
          {
            id: PRIORITY_VARIANT_ID,
            quantity: 1,
          },
        ],
      }
    : {
        // Use the real cart line key instead of only the variant ID.
        // This is more reliable when the drawer has been re-rendered.
        id: currentItem?.key || String(PRIORITY_VARIANT_ID),
        quantity: 0,
      };

  if (sectionId) {
    body.sections = sectionId;
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(
    url,
    fetchConfig('json', {
      body: JSON.stringify(body),
    })
  );

  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || 'No se pudo actualizar el envío prioritario');
  }

  let payload = {};
  try {
    payload = JSON.parse(responseText);
  } catch {
    payload = {};
  }

  const cart = await getCart();
  const sectionHtml = sectionId ? payload?.sections?.[sectionId] || null : null;

  return { cart, sectionHtml };
}

class RefynPriorityShipping extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-refyn-priority-toggle]');
    this.card = this.querySelector('[data-refyn-priority-card]');

    if (this.toggle instanceof HTMLButtonElement) {
      this.toggle.addEventListener('click', this.onToggle);
    }

    if (this.card instanceof HTMLElement) {
      this.card.addEventListener('click', this.onCardClick);
    }

    this.syncState();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggle);
    this.card?.removeEventListener('click', this.onCardClick);
  }

  setToggle(active) {
    if (!(this.toggle instanceof HTMLButtonElement)) return;
    this.toggle.setAttribute('aria-checked', active ? 'true' : 'false');
    this.toggle.setAttribute(
      'aria-label',
      active ? 'Quitar envío prioritario' : 'Agregar envío prioritario'
    );
  }

  onCardClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-refyn-priority-toggle]')) return;
    this.onToggle(event);
  };

  setError(message = '') {
    const box = this.querySelector('[data-refyn-priority-error]');
    if (!(box instanceof HTMLElement)) return;
    box.hidden = !message;
    box.textContent = message;
  }

  syncState = async () => {
    try {
      let cart = await getCart();
      cart = await clearCheckoutOnlyDiscounts(cart);
      this.setToggle(Boolean(findPriorityItem(cart)));
      await updateHeaderCartCount(cart);
    } catch (error) {
      console.warn('[REFYN] Priority state sync failed', error);
    }
  };

  onToggle = async (event) => {
    event.preventDefault();

    if (!(this.toggle instanceof HTMLButtonElement)) return;
    if (priorityMutationPromise) return;

    const wasActive = this.toggle.getAttribute('aria-checked') === 'true';
    const desiredActive = !wasActive;

    this.setError('');

    // Feedback visual instantáneo: el switch cambia antes de esperar la red.
    this.setToggle(desiredActive);
    this.setAttribute('aria-busy', 'true');

    priorityMutationPromise = (async () => {
      let before = await getCart();
      before = await clearCheckoutOnlyDiscounts(before);

      const sectionId = this.dataset.sectionId || '';
      const result = await setPriorityState(desiredActive, before, sectionId);
      const after = result.cart;
      const activeNow = Boolean(findPriorityItem(after));

      if (activeNow !== desiredActive) {
        throw new Error('Shopify devolvió un estado inesperado del carrito');
      }

      this.setToggle(activeNow);
      await updateHeaderCartCount(after);
      await refreshCartSection(this, result.sectionHtml);

      return after;
    })();

    try {
      await priorityMutationPromise;
    } catch (error) {
      console.warn('[REFYN] Priority toggle failed', error);

      this.setToggle(
        this.toggle?.getAttribute('aria-checked') === 'true' ? false : true
      );

      const cart = await getCart().catch(() => null);
      if (cart) this.setToggle(Boolean(findPriorityItem(cart)));

      this.setError('No pudimos actualizar el envío prioritario. Probá nuevamente.');
    } finally {
      priorityMutationPromise = null;
      this.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('refyn-priority-shipping')) {
  customElements.define('refyn-priority-shipping', RefynPriorityShipping);
}
